/** Mic capture for lab and interview room. One AudioContext per stream. */

const TARGET_RATE = 16000;
const FIRST_CHUNK_MS = 2200;
const CHUNK_MS = 3200;
const FLUSH_MIN_MS = 250;
const SILENCE_RMS = 0.00004;

function mergeFloat32(chunks) {
  const total = chunks.reduce((n, part) => n + part.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  chunks.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

function resampleLinear(input, fromRate, toRate = TARGET_RATE) {
  if (!input?.length) return new Float32Array(0);
  if (!fromRate || Math.abs(fromRate - toRate) < 1) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = src - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

function encodeWavPcm16(float32, sampleRate = TARGET_RATE) {
  const n = float32.length;
  const buffer = new ArrayBuffer(44 + n * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + n * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, n * 2, true);
  let offset = 44;
  for (let i = 0; i < n; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

function rmsOf(samples) {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}

function encodePcm16Le(float32) {
  const n = float32.length;
  const buffer = new ArrayBuffer(n * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < n; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

export function createLabMic({ getStream, shouldCapture, onWav, onPcm, onLevel, getAudioContext }) {
  let ctx = null;
  let ownedCtx = false;
  let source = null;
  let processor = null;
  let mute = null;
  let chunks = [];
  let chunkSamples = 0;
  let stopped = false;
  let firstClip = true;
  let lastProcessAt = 0;
  let connecting = false;

  const emitIfVoice = (samples, minMs) => {
    if (!samples.length) return;
    const sr = ctx?.sampleRate || 48000;
    const durationMs = (samples.length / sr) * 1000;
    if (durationMs < minMs) return;
    const rms = rmsOf(samples);
    if (rms < SILENCE_RMS) return;
    const wav = encodeWavPcm16(resampleLinear(samples, sr, TARGET_RATE), TARGET_RATE);
    if (wav.size > 200) onWav?.(wav, { rms, durationMs });
  };

  const disconnectGraph = () => {
    try { processor?.disconnect(); } catch (_) { /* ignore */ }
    try { source?.disconnect(); } catch (_) { /* ignore */ }
    try { mute?.disconnect(); } catch (_) { /* ignore */ }
    processor = null;
    source = null;
    mute = null;
  };

  const attachProcessor = (stream) => {
    disconnectGraph();
    source = ctx.createMediaStreamSource(stream);
    processor = ctx.createScriptProcessor(4096, 1, 1);
    mute = ctx.createGain();
    mute.gain.value = 0.0001;
    processor.onaudioprocess = (event) => {
      if (stopped) return;
      lastProcessAt = Date.now();
      if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      const input = event.inputBuffer.getChannelData(0);
      let peak = 0;
      for (let i = 0; i < input.length; i += 1) {
        const a = Math.abs(input[i]);
        if (a > peak) peak = a;
      }
      onLevel?.(Math.min(100, Math.round(peak * 220)));
      if (!shouldCapture()) {
        firstClip = true;
        chunks = [];
        chunkSamples = 0;
        return;
      }
      const copy = new Float32Array(input.length);
      copy.set(input);
      if (onPcm) {
        const pcm = resampleLinear(copy, ctx.sampleRate || 48000, TARGET_RATE);
        if (pcm.length) onPcm(encodePcm16Le(pcm));
      }
      if (!onWav) return;
      chunks.push(copy);
      chunkSamples += copy.length;
      const windowMs = firstClip ? FIRST_CHUNK_MS : CHUNK_MS;
      const needed = Math.floor(((ctx.sampleRate || 48000) * windowMs) / 1000);
      if (chunkSamples >= needed) {
        const merged = mergeFloat32(chunks);
        chunks = [];
        chunkSamples = 0;
        firstClip = false;
        emitIfVoice(merged, windowMs * 0.35);
      }
    };
    source.connect(processor);
    processor.connect(mute);
    mute.connect(ctx.destination);
    lastProcessAt = Date.now();
  };

  const start = async () => {
    if (stopped || connecting) return Boolean(ctx);
    const stream = getStream?.();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!stream || !AudioCtx || !stream.getAudioTracks?.().length) return false;
    connecting = true;
    try {
      const shared = getAudioContext?.();
      if (shared && shared.state !== 'closed') {
        ctx = shared;
        ownedCtx = false;
      } else if (!ctx || ctx.state === 'closed') {
        ctx = new AudioCtx();
        ownedCtx = true;
      }
      if (ctx.state === 'suspended') await ctx.resume();
      if (!stream.getAudioTracks().some((t) => t.readyState === 'live')) return false;
      attachProcessor(stream);
      return true;
    } catch (_) {
      disconnectGraph();
      return false;
    } finally {
      connecting = false;
    }
  };

  const ensureRunning = async () => {
    if (stopped) return;
    try {
      if (ctx?.state === 'suspended') await ctx.resume();
    } catch (_) { /* ignore */ }
    const stale = lastProcessAt && Date.now() - lastProcessAt > 1800;
    if (!ctx || ctx.state === 'closed' || stale || !processor) await start();
  };

  const flush = () => {
    if (!chunks.length) return;
    const merged = mergeFloat32(chunks);
    chunks = [];
    chunkSamples = 0;
    emitIfVoice(merged, FLUSH_MIN_MS);
  };

  const stop = () => {
    stopped = true;
    disconnectGraph();
    if (ownedCtx && ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
    ctx = null;
    ownedCtx = false;
    chunks = [];
    chunkSamples = 0;
    firstClip = true;
    lastProcessAt = 0;
  };

  return { start, flush, stop, ensureRunning };
}
